import { useEffect } from 'react';
import { useSelection } from './selection';
import { useData } from './data';
import { supabase } from './supabase';
import { READONLY } from './readonly';
import { useToast } from './toast';
import { useQuickAdd } from './quickAdd';

// Global keyboard shortcuts. Fires only when no input is focused.
//
//   j / ↓     — next task in current context
//   k / ↑     — previous task
//   c         — toggle complete on focused task
//   e         — edit (focus title when on TaskView)
//   n         — add task (open AddTaskForm)
//   /         — open Cmd+K search
//   g h       — go home
//   g m       — go mine
//   g u       — go uptime
//   ?         — show this help

export function KeyboardShortcuts() {
  const { selection, select, toggleActivity } = useSelection();
  const { tasks, allTasks } = useData();
  const { push } = useToast();
  const { show: showQuickAdd } = useQuickAdd();

  useEffect(() => {
    let lastG = 0;

    const isTyping = () => {
      const el = document.activeElement as HTMLElement | null;
      if (!el) return false;
      const tag = el.tagName;
      return (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        el.isContentEditable
      );
    };

    const onKey = async (e: KeyboardEvent) => {
      if (isTyping()) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      // g-prefix for "go to" shortcuts
      if (e.key === 'g' && !e.shiftKey) {
        lastG = Date.now();
        return;
      }
      if (lastG && Date.now() - lastG < 1500) {
        lastG = 0;
        if (e.key === 'h') {
          select({ type: 'home' });
          return;
        }
        if (e.key === 'm') {
          select({ type: 'my-tasks' });
          return;
        }
        if (e.key === 'u') {
          select({ type: 'uptime' });
          return;
        }
        if (e.key === 'a') {
          toggleActivity();
          return;
        }
      }

      // / — open search (CommandK handles its own listener, noop here)
      if (e.key === '/') return;

      // n — open quick-add modal
      if (e.key === 'n' && !e.shiftKey) {
        e.preventDefault();
        showQuickAdd();
        return;
      }

      // ? — toast with help
      if (e.key === '?') {
        push({
          kind: 'info',
          title: 'Keyboard shortcuts',
          body: 'j/k next/prev · c complete · e edit · n new · / search · g h/m/u go · ⌘K search',
          ttlMs: 8000,
        });
        return;
      }

      // Task complete — only on TaskView
      if ((e.key === 'c' || e.key === 'x') && selection.type === 'task') {
        if (READONLY) return;
        const task = allTasks.find((t) => t.id === selection.id);
        if (!task) return;
        const next = task.status === 'completed' ? 'pending' : 'completed';
        await supabase
          .from('tb_tasks')
          .update({
            status: next,
            completed_at: next === 'completed' ? new Date().toISOString() : null,
          })
          .eq('id', task.id);
        push({
          kind: next === 'completed' ? 'success' : 'info',
          title: next === 'completed' ? 'Task completed' : 'Task reopened',
          body: task.title.slice(0, 60),
          ttlMs: 2500,
        });
        return;
      }

      // j/k navigate priority queue on home, or tasks inside a project
      if (e.key === 'j' || e.key === 'ArrowDown' || e.key === 'k' || e.key === 'ArrowUp') {
        const dir = e.key === 'j' || e.key === 'ArrowDown' ? 1 : -1;
        // Build a list of task ids in the current context
        let pool: string[] = [];
        if (selection.type === 'home' || selection.type === 'my-tasks') {
          pool = tasks
            .filter((t) => t.parent_task_id === null && t.status !== 'completed')
            .map((t) => t.id);
        } else if (selection.type === 'project') {
          pool = tasks
            .filter(
              (t) => t.project_slug === selection.slug && t.parent_task_id === null,
            )
            .map((t) => t.id);
        } else if (selection.type === 'task') {
          // Find sibling tasks in the same project
          const current = tasks.find((t) => t.id === selection.id);
          if (!current) return;
          pool = tasks
            .filter(
              (t) =>
                t.project_slug === current.project_slug && t.parent_task_id === null,
            )
            .map((t) => t.id);
        }
        if (pool.length === 0) return;
        e.preventDefault();
        if (selection.type === 'task') {
          const idx = pool.indexOf(selection.id);
          const next = pool[(idx + dir + pool.length) % pool.length];
          if (next) select({ type: 'task', id: next });
        } else {
          const first = dir > 0 ? pool[0] : pool[pool.length - 1];
          if (first) select({ type: 'task', id: first });
        }
        return;
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selection, select, toggleActivity, tasks, allTasks, push, showQuickAdd]);

  return null;
}
